import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Plus, Search, SlidersHorizontal, X } from 'lucide-react';

export type PerformanceBucket =
  | 'top_decile'
  | 'top_quartile'
  | 'median'
  | 'bottom_quartile'
  | 'unscored';

export interface ExtendedFilters {
  channel: string[];
  source_type: string[];
  performance_bucket: PerformanceBucket | null;
  strategic_playbook_id: string[];
  group_by: 'flat' | 'playbook';
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'superseded', label: 'Superseded' },
];

const SOURCE_TYPES: { value: string; label: string }[] = [
  { value: 'ad_pipeline', label: 'Ad Pipeline' },
  { value: 'persona_enriched', label: 'Persona Enriched' },
  { value: 'freeform_input', label: 'Freeform' },
  { value: 'screenshot_extraction', label: 'Screenshot' },
  { value: 'manual', label: 'Manual' },
];

const PERF_BUCKETS: { value: PerformanceBucket; label: string }[] = [
  { value: 'top_decile', label: 'Top 10%' },
  { value: 'top_quartile', label: 'Top 25%' },
  { value: 'median', label: 'Median' },
  { value: 'bottom_quartile', label: 'Bottom 25%' },
  { value: 'unscored', label: 'Unscored' },
];

interface Props {
  query: string;
  onQueryChange: (q: string) => void;
  status: string;
  onStatusChange: (s: string) => void;
  extended: ExtendedFilters;
  onExtendedChange: (next: ExtendedFilters) => void;
  onNewBrief?: () => void;
}

export function BriefSearchBar({
  query,
  onQueryChange,
  status,
  onStatusChange,
  extended,
  onExtendedChange,
  onNewBrief,
}: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [localQuery, setLocalQuery] = useState(query);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  const handleQueryChange = (q: string) => {
    setLocalQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onQueryChange(q), 280);
  };

  const patchExtended = (p: Partial<ExtendedFilters>) =>
    onExtendedChange({ ...extended, ...p });

  const toggleSourceType = (value: string) => {
    const list = extended.source_type;
    const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    patchExtended({ source_type: next });
  };

  const activeFilterCount =
    extended.source_type.length +
    (extended.performance_bucket ? 1 : 0) +
    extended.strategic_playbook_id.length +
    (status ? 1 : 0);

  const clearAll = () => {
    onQueryChange('');
    setLocalQuery('');
    onStatusChange('');
    onExtendedChange({
      channel: [],
      source_type: [],
      performance_bucket: null,
      strategic_playbook_id: [],
      group_by: extended.group_by,
    });
  };

  return (
    <div className="space-y-3">
      {/* ── Search Row ── */}
      <div className="flex items-center gap-2">
        {/* Search box */}
        <div className="relative group flex-1 min-w-0">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-teal-400/20 to-cyan-400/20 blur-sm opacity-0 group-focus-within:opacity-100 transition-opacity" />
          <div className="relative flex items-center bg-white border border-slate-200 group-focus-within:border-teal-400/60 rounded-2xl shadow-sm transition-all overflow-hidden">
            <Search
              size={16}
              className="absolute left-4 text-slate-400 group-focus-within:text-teal-500 transition-colors pointer-events-none"
            />
            <input
              value={localQuery}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder={
              extended.group_by === 'playbook'
                ? 'Search playbooks by name, client, ID…'
                : 'Search briefs by name, persona, brief ID…'
            }
              className="flex-1 pl-11 pr-4 py-3.5 text-sm bg-transparent placeholder-slate-400 focus:outline-none text-slate-700"
            />
            {localQuery && (
              <button
                onClick={() => handleQueryChange('')}
                className="mr-3 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={14} />
              </button>
            )}

            {/* Status select */}
            <div className="h-7 w-px bg-slate-100 mr-3" />
            <select
              value={status}
              onChange={(e) => onStatusChange(e.target.value)}
              className="mr-3 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/40"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* View mode toggle: Briefs / Playbooks */}
            <div className="hidden md:flex items-center bg-slate-100 rounded-lg p-0.5 mr-3 gap-0.5">
              {([
                { value: 'flat', label: 'Briefs' },
                { value: 'playbook', label: 'Playbooks' },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => patchExtended({ group_by: value })}
                  className={clsx(
                    'px-3 py-1 text-[11px] font-semibold rounded-md transition-all whitespace-nowrap',
                    extended.group_by === value
                      ? 'bg-white text-teal-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="h-7 w-px bg-slate-100 mr-3" />

            {/* Filters button */}
            <button
              onClick={() => setShowAdvanced((p) => !p)}
              className={clsx(
                'flex items-center gap-1.5 mr-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                showAdvanced
                  ? 'bg-teal-50 text-teal-700'
                  : 'text-slate-500 hover:bg-slate-50',
              )}
            >
              <SlidersHorizontal size={12} />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-teal-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {activeFilterCount > 0 && (
              <button
                onClick={clearAll}
                className="mr-3 flex items-center gap-1 text-[11px] text-slate-400 hover:text-red-500 transition-colors"
              >
                <X size={10} />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* New Brief button — outside the search box, pinned right */}
        {onNewBrief && (
          <button
            onClick={onNewBrief}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-3 text-sm font-medium text-white bg-teal-500 rounded-2xl hover:bg-teal-600 transition-colors shadow-sm"
          >
            <Plus size={14} />
            New Brief
          </button>
        )}
      </div>

      {/* ── Advanced Filters Panel ── */}
      {showAdvanced && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Source Type */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
                Source Type
              </label>
              <div className="flex flex-wrap gap-1">
                {SOURCE_TYPES.map((s) => {
                  const active = extended.source_type.includes(s.value);
                  return (
                    <button
                      key={s.value}
                      onClick={() => toggleSourceType(s.value)}
                      className={clsx(
                        'px-2 py-1 rounded-lg text-[11px] font-medium border transition-all',
                        active
                          ? 'bg-purple-500 border-purple-500 text-white shadow-sm'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-purple-300 hover:text-purple-600',
                      )}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => onStatusChange(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/40 bg-white"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Performance Bucket */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
                Performance Bucket
              </label>
              <select
                value={extended.performance_bucket ?? ''}
                onChange={(e) =>
                  patchExtended({
                    performance_bucket:
                      (e.target.value || null) as PerformanceBucket | null,
                  })
                }
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/40 bg-white"
              >
                <option value="">Any performance</option>
                {PERF_BUCKETS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Grouping */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
                Grouping
              </label>
              <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden">
                {([
                  { value: 'flat', label: 'Briefs' },
                  { value: 'playbook', label: 'Playbooks' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => patchExtended({ group_by: value })}
                    className={clsx(
                      'px-3 py-1.5 text-[11px] font-medium transition-colors',
                      extended.group_by === value
                        ? 'bg-teal-500 text-white'
                        : 'bg-white text-slate-500 hover:bg-slate-50',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
